/**
 * Dark-mode theme resolution (CH-11 tri-state), shared between:
 * - the zustand store (src/store/appStore.ts) — the runtime source of
 *   truth, which imports the pure helpers below rather than restating them,
 * - this file's own one-shot boot-time DOM sync (`initDarkMode`, called from
 *   src/main.tsx / src/web/main.web.tsx before React mounts),
 * - and (by hand-kept parity, not import — it runs before any JS module can
 *   load) index.html's inline pre-paint `<script>`.
 *
 * `initDarkMode()` re-applies the same class the pre-paint script already
 * set, so it's purely idempotent. Live "system" follow-through AFTER boot is
 * owned by the store's own listener (wired via `watchSystemThemeChanges`
 * below, called from appStore.ts) — this file does not register its own, to
 * avoid two sources of truth racing each other.
 */

// Theme tri-state (CH-11). Single canonical declaration — import this
// everywhere else rather than restating the union.
export type ThemeMode = 'system' | 'light' | 'dark';

export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Resolve the tri-state mode to the effective boolean the rest of the app renders. */
export function resolveIsDarkMode(mode: ThemeMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return getSystemPrefersDark();
}

/**
 * Legacy boolean -> tri-state migration (persisted-store v2 -> v3). `true` ->
 * `'dark'`, `false` -> `'light'` — an explicit prior choice is never silently
 * reinterpreted as "follow system", which would change a light-mode user's
 * appearance out from under them. A genuinely missing/invalid legacy value
 * (fresh install) defaults to `'system'`.
 *
 * Exported so the mapping is unit-testable without exercising the full
 * zustand persist/rehydrate pipeline.
 */
export function migrateLegacyDarkMode(legacyDarkMode: unknown): ThemeMode {
  if (typeof legacyDarkMode === 'boolean') {
    return legacyDarkMode ? 'dark' : 'light';
  }
  return 'system';
}

/** `ui:theme_mode` fires only on a real mode change, for all three values (CLAUDE.md mode-switch convention). */
export function trackThemeModeChange(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics?.track('ui:theme_mode', { mode }).catch(() => {
    /* ignore — web mode or IPC unavailable */
  });
}

/**
 * Registers ONE `prefers-color-scheme` change listener for the life of the
 * page. Callers must call this once (e.g. right after store creation), not
 * on every mode toggle, or listeners accumulate — this function does not
 * return an unsubscribe handle because nothing in this app ever needs one.
 * `onChange(isDark)` fires only when `shouldFollow()` is true at the moment
 * the OS preference flips (the store passes "is themeMode still 'system'
 * right now"), so a listener that has become irrelevant simply no-ops
 * instead of needing to be torn down and re-added. No-ops entirely if
 * matchMedia is unavailable (SSR, unsupported environment).
 */
export function watchSystemThemeChanges(
  shouldFollow: () => boolean,
  onChange: (isDark: boolean) => void,
): void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  try {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', () => {
      if (!shouldFollow()) return;
      onChange(getSystemPrefersDark());
    });
  } catch {
    // matchMedia / addEventListener unsupported in this environment — ignore.
  }
}

// Resolve the persisted theme mode from localStorage, understanding both the
// current shape (`state.themeMode`) and the legacy boolean shape
// (`state.darkMode`) — zustand's `persist` middleware only rewrites
// localStorage on the next `set()` call, so an unmigrated blob from before
// this app version can sit there indefinitely.
function getPersistedThemeMode(): ThemeMode | null {
  try {
    const stored = localStorage.getItem('sql-studio-app-store');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const state = parsed?.state;
    if (
      state?.themeMode === 'system' ||
      state?.themeMode === 'light' ||
      state?.themeMode === 'dark'
    ) {
      return state.themeMode;
    }
    if (typeof state?.darkMode === 'boolean') {
      return state.darkMode ? 'dark' : 'light';
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

// Initialize dark mode based on persisted preference (tri-state, migrated
// from the legacy boolean) or system preference when nothing is persisted.
export function initDarkMode() {
  const mode = getPersistedThemeMode() ?? 'system';
  const shouldBeDark = resolveIsDarkMode(mode);

  if (shouldBeDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}
